import React, { useState } from 'react';
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { getPlayerFundFlowDetail, confirmPlayerFundFlow } from 'api/playerfundflow';

const IMAGE_PROXY_URL = process.env.REACT_APP_IMAGE_PROXY_URL || 'http://localhost:3001';

const PlayerFundFlow: React.FC = () => {
    const queryClient = useQueryClient();
    const { fundFlowId } = useParams();
    const [imageOpen, setImageOpen] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    const detailQuery = useQuery(['playerfundflow', fundFlowId], getPlayerFundFlowDetail, {
        enabled: !!fundFlowId,
    });

    const confirmMutation = useMutation(() => confirmPlayerFundFlow(fundFlowId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['playerfundflow', fundFlowId]);
        }
    });

    return (
        <div className="col-span-12">
            <div className="w-full h-full bg-white shadow-md p-5 rounded" style={{ minHeight: 500 }}>
                {detailQuery.isLoading ? (
                    <div className="flex items-center justify-center h-full" style={{ minHeight: 500 }}>
                        <CircularProgress />
                    </div>
                ) : (<>
                <p className="text-lg font-medium mb-8">Движение ЛС №{fundFlowId}</p>

                <p className="text-lg font-light mb-4">
                    Дата создания: {moment(detailQuery.data?.created_datetime).format('DD MMM YYYY HH:mm')}
                </p>
                <p className="text-lg font-light mb-4">
                    Игрок: {detailQuery.data?.player_username}
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded py-2 mb-4 inline-block" style={{ marginLeft: '-0.75rem', paddingLeft: '0.75rem', paddingRight: '0.75rem' }}>
                    <p className="text-lg font-light mb-1">
                        Рум: <span className="font-medium">{detailQuery.data?.room_name}</span>
                    </p>
                    <p className="text-lg font-light mb-1">
                        Ник в руме: <span className="font-medium">{detailQuery.data?.room_account_name}</span>
                    </p>
                    <p className="text-lg font-light mb-1">
                        Баланс рума: <span className="font-medium">{detailQuery.data?.room_balance} {detailQuery.data?.chip_currency_name}</span>
                    </p>
                    <p className="text-lg font-light">
                        Сумма: <span className="font-medium">{detailQuery.data?.amount} {detailQuery.data?.chip_currency_name}</span>
                    </p>
                </div>
                <p className="text-lg font-light mb-4">
                    Тип: {detailQuery.data?.fund_type}
                </p>
                <p className="text-lg font-light mb-4">
                    Комментарий:{' '}
                    {/(https?:\/\/[^\s]+)/.test(detailQuery.data?.comment || '') ? (() => {
                        const url = detailQuery.data.comment.match(/(https?:\/\/[^\s]+)/)![0];
                        const proxyUrl = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(url)}`;
                        return (
                            <>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                    {detailQuery.data?.comment}
                                </a>
                                <div
                                    className="mt-2 cursor-pointer inline-block"
                                    onClick={() => { setImageUrl(proxyUrl); setImageOpen(true); }}
                                >
                                    <img
                                        src={proxyUrl}
                                        alt="Скриншот"
                                        className="rounded border border-gray-300 hover:border-blue-400 transition-colors"
                                        style={{ maxWidth: 300, maxHeight: 200 }}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                </div>
                            </>
                        );
                    })() : detailQuery.data?.comment}
                </p>
                {detailQuery.data && (
                    <p className="text-lg font-light mb-4">
                        Статус: <span className={`px-2 rounded ${detailQuery.data.is_processed ? 'bg-green-200' : 'bg-amber-500'}`} style={{ paddingTop: '1.2px', paddingBottom: '1.2px' }}>{detailQuery.data.is_processed ? 'processed' : 'pending'}</span>
                    </p>
                )}
                {detailQuery.data?.processed_datetime && (
                    <p className="text-lg font-light mb-4">
                        Дата обработки: {moment(detailQuery.data?.processed_datetime).format('DD MMM YYYY HH:mm')}
                    </p>
                )}

                {!detailQuery.data?.is_processed && (
                    <div className="pt-4">
                        <Button
                            variant="contained"
                            color="success"
                            loading={confirmMutation.isLoading}
                            onClick={() => confirmMutation.mutate()}
                        >
                            Подтвердить
                        </Button>
                    </div>
                )}

                <Dialog
                    open={imageOpen}
                    onClose={() => setImageOpen(false)}
                    maxWidth={false}
                    PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
                >
                    <IconButton
                        onClick={() => setImageOpen(false)}
                        sx={{ position: 'absolute', top: -40, right: 0, color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={imageUrl}
                        alt="Скриншот"
                        style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }}
                    />
                </Dialog>
                </>)}
            </div>
        </div>
    );
};

export default PlayerFundFlow;
